import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, type ApiClientProject } from "../lib/api";
import { useClientAuth } from "../lib/clientAuth";
import { ReviewSurface } from "../components/ReviewSurface";
import { usePages } from "../hooks/usePages";
import {
  OnboardingTour,
  type TourStep,
} from "../components/OnboardingTour";
import {
  readOnboardingComplete,
  writeOnboardingComplete,
} from "../lib/onboarding";
import { SidebarToggle } from "../components/SidebarToggle";

// Shared with the owner project view + anonymous share view so a
// collaborator's collapsed preference survives across contexts on the
// same browser.
const PAGES_HIDDEN_STORAGE_KEY = "clarity_pages_hidden";

function loadPagesHidden(): boolean {
  try {
    return localStorage.getItem(PAGES_HIDDEN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function savePagesHidden(hidden: boolean) {
  try {
    if (hidden) localStorage.setItem(PAGES_HIDDEN_STORAGE_KEY, "1");
    else localStorage.removeItem(PAGES_HIDDEN_STORAGE_KEY);
  } catch {
    // ignore — storage may be unavailable in private/sandboxed contexts
  }
}

// Phase 9 — Onboarding tour steps. Anchored to elements via data-tour-id
// attributes added in ProxyFrame, ViewportToolbar, and CommentSidebar.
// Falls back to a centred popover when an anchor isn't present (e.g. no
// comments yet for the Reply / Edit steps).
const TOUR_STEPS: TourStep[] = [
  {
    id: "click",
    anchorSelector: '[data-tour-id="iframe"]',
    title: "Click to comment",
    body: "Click anywhere on the page to drop a comment pin. Avista sees it right away.",
  },
  {
    id: "reply",
    anchorSelector: '[data-tour-id="comment-reply"]',
    title: "Reply to a thread",
    body: "Each comment has a Reply action. Use it to keep conversations threaded under the original.",
  },
  {
    id: "edit",
    anchorSelector: '[data-tour-id="comment-edit"]',
    title: "Edit your own comments",
    body: "You can edit any comment you wrote — useful for typos or follow-ups.",
  },
  {
    id: "viewport",
    anchorSelector: '[data-tour-id="viewport"]',
    title: "Change screen size",
    body: "Preview the page at Desktop, Tablet, or Mobile widths from the toolbar above.",
  },
  {
    id: "comments",
    anchorSelector: '[data-tour-id="comments-toggle"]',
    title: "Open all comments",
    body: "The page list and review surface stay distraction-free by default. Click the comment-bubble icon to slide in the full list of comments for the current page.",
  },
];

type LoadState =
  | { status: "loading" }
  | { status: "ready"; project: ApiClientProject }
  | { status: "not_invited" }
  | { status: "error" };

// Client-side project view. Renders the same ReviewSurface a guest
// reviewer sees (mode="share"), but pre-fills identity from the client
// session so the composer never asks for name+email.

export function ClientProjectPage() {
  const { id } = useParams<{ id: string }>();
  const { state } = useClientAuth();
  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [pagesHidden, setPagesHiddenState] = useState<boolean>(loadPagesHidden);
  const [currentPath, setCurrentPath] = useState<string>("");

  const setPagesHidden = useCallback((next: boolean) => {
    setPagesHiddenState(next);
    savePagesHidden(next);
  }, []);

  // Onboarding tour fires once per browser. We delay a few hundred ms
  // after the project loads so the iframe + sidebar have actually painted
  // and getBoundingClientRect on the iframe anchor returns a real size.
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    if (load.status !== "ready") return;
    if (readOnboardingComplete()) return;
    const t = window.setTimeout(() => setShowTour(true), 600);
    return () => window.clearTimeout(t);
  }, [load.status]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoad({ status: "loading" });
    api
      .clientListProjects()
      .then((res) => {
        if (cancelled) return;
        const match = res.projects.find((p) => p.id === id);
        if (match) setLoad({ status: "ready", project: match });
        else setLoad({ status: "not_invited" });
      })
      .catch(() => {
        if (!cancelled) setLoad({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // usePages stays a top-level hook — it tolerates an empty token while
  // we're loading the project lookup.
  const pages = usePages(
    load.status === "ready"
      ? { mode: "share", shareToken: load.project.shareToken }
      : { mode: "share", shareToken: "" },
  );

  if (load.status === "loading") return <Centered>Loading review…</Centered>;
  if (load.status === "not_invited")
    return (
      <Centered>
        <p>You no longer have access to this project.</p>
        <Link to="/c/dashboard" className="mt-3 inline-block text-sm underline">
          Back to your reviews
        </Link>
      </Centered>
    );
  if (load.status === "error") return <Centered>Something went wrong.</Centered>;
  if (state.status !== "authed") return null;

  const project = load.project;
  const identity = {
    name: state.me.name ?? state.me.email,
    email: state.me.email,
  };

  const showLeftSidebar = !pagesHidden;
  const showFloatingToggle = pagesHidden;

  return (
    <section className="flex h-[calc(100vh-3.5rem)]">
      {showLeftSidebar ? (
        <div className="flex w-72 shrink-0 flex-col overflow-y-auto border-r border-neutral-200 bg-neutral-50 px-5 py-6">
          <div className="flex items-center justify-between">
            <Link
              to="/c/dashboard"
              className="text-xs text-neutral-500 hover:text-neutral-900"
            >
              ← Your reviews
            </Link>
            <SidebarToggle
              hidden={false}
              onToggle={() => setPagesHidden(true)}
              variant="edge"
            />
          </div>
          <h1 className="mt-3 truncate text-base font-semibold">{project.name}</h1>
          <a
            href={project.targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 block truncate text-xs text-neutral-500 hover:underline"
          >
            {project.targetUrl}
          </a>
          <div className="mt-6 text-xs text-neutral-500">
            Click anywhere on the page to leave a comment.
          </div>
          <div className="mt-6">
            <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Pages
            </div>
            <ul className="mt-2 space-y-0.5">
              {pages.pages.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setCurrentPath(p.path)}
                    className={
                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition " +
                      (p.path === currentPath
                        ? "bg-neutral-900 text-white"
                        : "text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900")
                    }
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">{p.name}</span>
                    {p.openCommentCount > 0 ? (
                      <span
                        className={
                          "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold " +
                          (p.path === currentPath
                            ? "bg-white/20 text-white"
                            : "bg-neutral-200 text-neutral-700")
                        }
                      >
                        {p.openCommentCount}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      <div className="relative min-w-0 flex-1">
        {showFloatingToggle ? (
          <SidebarToggle
            hidden={true}
            onToggle={() => setPagesHidden(false)}
            variant="floating"
          />
        ) : null}
        <ReviewSurface
          mode="share"
          projectId={project.id}
          shareToken={project.shareToken}
          defaultIdentity={identity}
          pages={pages.pages}
          currentPath={currentPath}
          onSetCurrentPath={setCurrentPath}
          onCommentsChanged={pages.reload}
        />
      </div>

      {showTour ? (
        <OnboardingTour
          steps={TOUR_STEPS}
          onClose={() => {
            writeOnboardingComplete();
            setShowTour(false);
          }}
          onComplete={() => {
            writeOnboardingComplete();
            setShowTour(false);
          }}
        />
      ) : null}
    </section>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md px-6 py-20 text-center text-neutral-600">
      {children}
    </div>
  );
}
